import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchBorrowers,
  fetchBorrower,
  fetchBorrowerTypes,
  createBorrower,
  updateBorrower,
  deleteBorrower,
  fetchLoans,
  fetchActiveLoan,
  fetchOverdueLoans,
  fetchLoanSummary,
  fetchLoanStatuses,
  fetchAvailableAssets,
  checkoutAsset,
  returnAsset,
  updateLoan,
  fetchAssetLoanHistory,
  BorrowerFilters,
  LoanFilters,
  CreateBorrowerRequest,
  UpdateBorrowerRequest,
  CheckoutRequest,
} from '../api/loans';

// ============================================================================
// Query Keys
// ============================================================================

export const loanQueryKeys = {
  borrowers: {
    all: ['borrowers'] as const,
    list: (filters?: BorrowerFilters) => ['borrowers', 'list', filters] as const,
    detail: (id: string) => ['borrowers', 'detail', id] as const,
    types: () => ['borrowers', 'types'] as const,
  },
  loans: {
    all: ['loans'] as const,
    list: (filters?: LoanFilters) => ['loans', 'list', filters] as const,
    detail: (id: string) => ['loans', 'detail', id] as const,
    active: () => ['loans', 'active'] as const,
    overdue: () => ['loans', 'overdue'] as const,
    summary: () => ['loans', 'summary'] as const,
    statuses: () => ['loans', 'statuses'] as const,
    availableAssets: () => ['loans', 'availableAssets'] as const,
    assetHistory: (assetId: string) => ['loans', 'assetHistory', assetId] as const,
  },
};

// ============================================================================
// Borrower Hooks
// ============================================================================

export function useBorrowers(filters?: BorrowerFilters) {
  return useQuery({
    queryKey: loanQueryKeys.borrowers.list(filters),
    queryFn: async () => {
      const response = await fetchBorrowers(filters);
      return response.borrowers;
    },
  });
}

export function useBorrower(id: string) {
  return useQuery({
    queryKey: loanQueryKeys.borrowers.detail(id),
    queryFn: () => fetchBorrower(id),
    enabled: !!id,
  });
}

export function useBorrowerTypes() {
  return useQuery({
    queryKey: loanQueryKeys.borrowers.types(),
    queryFn: async () => {
      const response = await fetchBorrowerTypes();
      return response.types;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreateBorrower() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBorrowerRequest) => createBorrower(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.borrowers.all });
    },
  });
}

export function useUpdateBorrower() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBorrowerRequest }) => updateBorrower(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.borrowers.all });
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.borrowers.detail(variables.id) });
    },
  });
}

export function useDeleteBorrower() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBorrower(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.borrowers.all });
    },
  });
}

// ============================================================================
// Loan Hooks
// ============================================================================

export function useLoans(filters?: LoanFilters) {
  return useQuery({
    queryKey: loanQueryKeys.loans.list(filters),
    queryFn: async () => {
      const response = await fetchLoans(filters);
      return response.loans;
    },
  });
}

export function useActiveLoans() {
  return useQuery({
    queryKey: loanQueryKeys.loans.active(),
    queryFn: async () => {
      const response = await fetchActiveLoan();
      return response.loans;
    },
    refetchInterval: 60000, // Refresh every minute to update overdue status
  });
}

export function useOverdueLoans() {
  return useQuery({
    queryKey: loanQueryKeys.loans.overdue(),
    queryFn: fetchOverdueLoans,
    refetchInterval: 60000, // Refresh every minute
  });
}

export function useLoanSummary() {
  return useQuery({
    queryKey: loanQueryKeys.loans.summary(),
    queryFn: fetchLoanSummary,
    refetchInterval: 60000, // Refresh every minute
  });
}

export function useLoanStatuses() {
  return useQuery({
    queryKey: loanQueryKeys.loans.statuses(),
    queryFn: async () => {
      const response = await fetchLoanStatuses();
      return response.statuses;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAvailableAssets() {
  return useQuery({
    queryKey: loanQueryKeys.loans.availableAssets(),
    queryFn: async () => {
      const response = await fetchAvailableAssets();
      return response.assets;
    },
  });
}

export function useCheckoutAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CheckoutRequest) => checkoutAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.loans.all });
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.borrowers.all });
    },
  });
}

export function useReturnAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, notes }: { loanId: string; notes?: string }) => returnAsset(loanId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.loans.all });
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.borrowers.all });
    },
  });
}

export function useUpdateLoan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, data }: { loanId: string; data: { dueDate?: string; notes?: string } }) =>
      updateLoan(loanId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loanQueryKeys.loans.all });
    },
  });
}

export function useAssetLoanHistory(assetId: string) {
  return useQuery({
    queryKey: loanQueryKeys.loans.assetHistory(assetId),
    queryFn: () => fetchAssetLoanHistory(assetId),
    enabled: !!assetId,
  });
}
